import React from 'react';
import { FluentProvider, teamsLightTheme, Text, Button } from '@fluentui/react-components';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Navigation } from './components/Common/Navigation/Navigation';
import { SupportStaffDashboard } from './components/SupportStaff/Dashboard/Dashboard';
import { DoctorDashboard } from './components/Doctor/Dashboard/DoctorDashboard';
import { TeamsConsultationInterface } from './components/Common/TeamsConsultation/TeamsConsultationInterface';
import { PatientSelectionInterface } from './components/Doctor/Consultation/PatientSelectionInterface';
import { useState, useEffect } from 'react';
import { Login } from './components/Auth/Login';
import { authService } from './services/auth/authService';
import { teamsService } from './services/teams/teamsService';
import { AnalyticsDashboard } from './components/Admin/Analytics/AnalyticsDashboard';
import DoctorPreferences from './components/Doctor/Preferences/DoctorPreferences';
import DoctorAvailability from './components/Doctor/Availability/DoctorAvailability';

interface UserContext {
    userRole: 'support' | 'doctor' | 'admin';
    userName: string;
    userId: string;
    email: string;
}

// Create a layout component that handles the navigation logic
const AppLayout: React.FC = () => {
    const location = useLocation();
    const isVideoCall = location.pathname === '/consult';
    const [userContext, setUserContext] = useState<UserContext | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // Check authentication and initialize Teams SDK
        const initializeApp = async () => {
            try {
                // Initialize Teams SDK first
                console.log('🔄 Initializing Teams SDK...');
                const teamsContext = await teamsService.initialize();

                if (teamsContext.isInTeams) {
                    console.log('✅ Running in Microsoft Teams');
                    console.log('Teams User:', teamsContext.user);
                    console.log('Teams Meeting:', teamsContext.meeting);

                    // If we're in a Teams meeting, check if it's a consultation
                    if (teamsContext.meeting) {
                        console.log(
                            '🎥 Detected Teams meeting context - consultation may be active'
                        );
                    }

                    // Try Teams SSO authentication first
                    try {
                        const teamsAuthResult = await authService.authenticateWithTeamsContext();
                        if (teamsAuthResult && teamsAuthResult.success) {
                            setUserContext({
                                userRole: teamsAuthResult.user.role,
                                userName:
                                    teamsAuthResult.user.displayName ||
                                    `${teamsAuthResult.user.firstName} ${teamsAuthResult.user.lastName}`,
                                userId: teamsAuthResult.user.id.toString(),
                                email: teamsAuthResult.user.email,
                            });
                            setIsLoading(false);
                            return;
                        }
                    } catch (teamsAuthError) {
                        console.warn(
                            '⚠️ Teams SSO authentication failed, falling back to standard auth'
                        );
                    }
                }

                // Fallback to standard authentication
                if (authService.isAuthenticated()) {
                    const userInfo = authService.getCurrentUser();
                    if (userInfo) {
                        setUserContext({
                            userRole: userInfo.role,
                            userName:
                                userInfo.displayName ||
                                `${userInfo.firstName} ${userInfo.lastName}`,
                            userId: userInfo.id.toString(),
                            email: userInfo.email,
                        });
                    }
                }
            } catch (error) {
                console.error('Failed to initialize application:', error);
            } finally {
                setIsLoading(false);
            }
        };

        initializeApp();
    }, []);

    const handleLoginSuccess = () => {
        const userInfo = authService.getCurrentUser();
        if (userInfo) {
            setUserContext({
                userRole: userInfo.role,
                userName: userInfo.displayName || `${userInfo.firstName} ${userInfo.lastName}`,
                userId: userInfo.id.toString(),
                email: userInfo.email,
            });
        }
    };

    // Show loading spinner while checking authentication
    if (isLoading) {
        return (
            <div
                style={{
                    height: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '16px',
                }}
            >
                <Text size={500}>Loading Baylor Psychiatry...</Text>
                <Text size={300} style={{ color: '#605e5c' }}>
                    Initializing Teams SDK and authentication...
                </Text>
            </div>
        );
    }

    // Show login if not authenticated
    if (!userContext) {
        return <Login onLoginSuccess={handleLoginSuccess} />;
    }

    // Check if we're in the legacy video call route
    if (isVideoCall) {
        // Get consultation ID from URL params if available
        const urlParams = new URLSearchParams(window.location.search);
        const consultationId = urlParams.get('id');

        if (consultationId) {
            // New path: Teams consultation interface
            return (
                <TeamsConsultationInterface
                    consultationId={consultationId}
                    onConsultationEnd={() => (window.location.href = '/')}
                />
            );
        }

        // Show patient selection interface for doctors
        if (userContext?.userRole === 'doctor') {
            return <PatientSelectionInterface />;
        }

        // For other roles, show the legacy migration message
        const teamsContext = teamsService.getContext();
        if (teamsContext.isInTeams) {
            return (
                <div
                    style={{
                        height: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '16px',
                        backgroundColor: '#1a1a1a',
                        color: 'white',
                    }}
                >
                    <Text size={600} style={{ color: 'white' }}>
                        🎥 Consultation Active
                    </Text>
                    <Text size={400} style={{ color: '#cccccc' }}>
                        You're already in a Teams meeting. Use Teams meeting controls for this
                        consultation.
                    </Text>
                    <Button
                        appearance="primary"
                        onClick={() => window.history.back()}
                        style={{ marginTop: '16px' }}
                    >
                        Return to Dashboard
                    </Button>
                </div>
            );
        } else {
            // Standalone mode - redirect to dashboard with warning
            return (
                <div
                    style={{
                        height: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '16px',
                        backgroundColor: '#f3f2f1',
                    }}
                >
                    <Text size={600}>⚠️ Teams Meeting Required</Text>
                    <Text size={400} style={{ textAlign: 'center', maxWidth: '400px' }}>
                        Consultations now use Microsoft Teams meetings. Please start consultations
                        from the dashboard to create Teams meetings.
                    </Text>
                    <Button
                        appearance="primary"
                        onClick={() => (window.location.href = '/')}
                        style={{ marginTop: '16px' }}
                    >
                        Go to Dashboard
                    </Button>
                </div>
            );
        }
    }

    // Render normal application layout
    return (
        <div
            className="AppLayout"
            style={{ display: 'flex', flexDirection: 'row', width: '100%', height: '100%' }}
        >
            <Navigation userContext={userContext} />
            <div
                className="AppMainContent"
                style={{ flex: 1, overflow: 'auto', background: '#fff' }}
            >
                <Routes>
                    <Route
                        path="/"
                        element={
                            userContext.userRole === 'support' ? (
                                <SupportStaffDashboard />
                            ) : userContext.userRole === 'doctor' ? (
                                <DoctorDashboard />
                            ) : (
                                <AnalyticsDashboard />
                            )
                        }
                    />
                    {/* Support staff explicit tab routes */}
                    {userContext.userRole === 'support' && (
                        <>
                            <Route
                                path="/scheduling"
                                element={<SupportStaffDashboard activeTab="schedule" />}
                            />
                            <Route
                                path="/doctors"
                                element={<SupportStaffDashboard activeTab="doctors" />}
                            />
                            <Route
                                path="/settings"
                                element={<SupportStaffDashboard activeTab="settings" />}
                            />
                        </>
                    )}
                    <Route path="/analytics" element={<AnalyticsDashboard />} />
                    <Route path="/doctor-preferences" element={<DoctorPreferences />} />
                    <Route path="/doctor-availability" element={<DoctorAvailability />} />
                    {/* Add route for /consult */}
                    <Route path="/consult" element={<PatientSelectionInterface />} />
                </Routes>
            </div>
        </div>
    );
};

function App() {
    return (
        <FluentProvider theme={teamsLightTheme}>
            <Router>
                <Routes>
                    {/* All routes now handled by AppLayout - Teams meetings replace custom video interface */}
                    <Route path="/*" element={<AppLayout />} />
                </Routes>
            </Router>
        </FluentProvider>
    );
}

export default App;
